import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, ChevronDown, ChevronUp, Utensils } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const hu = (n) => Math.round(Number(n) || 0).toLocaleString('hu-HU');
const SLOT_LABEL = {
  reggeli: 'Reggeli', tizorai: 'Tízórai', ebed: 'Ebéd',
  uzsonna: 'Uzsonna', vacsora: 'Vacsora', egyeb: 'Egyéb',
};

/** Edzői nézet: az ügyfél étkezési naplója napi bontásban + heti átlaggal. */
const ClientFoodLogCard = ({ adminKey, userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    axios.get(`${API}/api/admin/clients/${userId}/food-log?days=14`,
      { headers: { 'X-Admin-Key': adminKey } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [adminKey, userId]);

  const t = data?.targets;
  const avg = data?.average;

  return (
    <div data-testid="admin-client-food-log" className="bg-white border border-gray-200 rounded-xl p-5">
      <h3 className="text-[11px] font-black uppercase tracking-[0.14em] text-gray-500 mb-4 flex items-center gap-2">
        <Utensils size={14} /> Étkezési napló (utolsó 14 nap)
      </h3>

      {loading ? (
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <Loader2 size={15} className="animate-spin" /> Betöltés…
        </div>
      ) : !data || data.logged_days === 0 ? (
        <p className="text-sm text-gray-500">Az ügyfél még nem naplózott étkezést.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="rounded-lg bg-gray-900 text-white p-3">
              <div className="text-[10px] uppercase tracking-wider text-gray-400">Napi átlag</div>
              <div className="text-2xl font-black" data-testid="food-log-avg-kcal">{hu(avg.kcal)}</div>
              <div className="text-[11px] text-gray-400">kcal / {data.logged_days} naplózott nap</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Fehérje átlag</div>
              <div className="text-xl font-black text-gray-900">{hu(avg.protein)} g</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Szénh. átlag</div>
              <div className="text-xl font-black text-gray-900">{hu(avg.carbs)} g</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Zsír átlag</div>
              <div className="text-xl font-black text-gray-900">{hu(avg.fat)} g</div>
            </div>
          </div>

          {t?.kcal > 0 && (
            <p className="text-xs text-gray-500 mb-3">
              Napi cél: <strong>{hu(t.kcal)} kcal</strong> ({t.label}) · eltérés az átlagtól:{' '}
              <strong className={avg.kcal > t.kcal ? 'text-red-600' : 'text-emerald-700'}>
                {avg.kcal > t.kcal ? '+' : ''}{hu(avg.kcal - t.kcal)} kcal
              </strong>
            </p>
          )}

          <div className="divide-y divide-gray-100 border-t border-gray-100">
            {data.days.map(d => (
              <div key={d.date}>
                <button
                  data-testid={`food-log-day-${d.date}`}
                  onClick={() => setOpen(open === d.date ? null : d.date)}
                  className="w-full flex items-center justify-between py-2.5 text-left hover:bg-gray-50 px-1"
                >
                  <div>
                    <div className="text-sm font-bold text-gray-900">{d.date}</div>
                    <div className="text-[11px] text-gray-500">
                      F {hu(d.protein)} g · CH {hu(d.carbs)} g · Zs {hu(d.fat)} g · {d.entries.length} tétel
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-black ${
                      t?.kcal > 0 && d.kcal > t.kcal ? 'text-red-600' : 'text-gray-900'}`}>
                      {hu(d.kcal)} kcal
                    </span>
                    {open === d.date ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </div>
                </button>
                {open === d.date && (
                  <ul className="pb-3 pl-1 space-y-1">
                    {d.entries.map(e => (
                      <li key={e.id} className="text-xs text-gray-600 flex justify-between gap-3">
                        <span className="truncate">
                          {e.name}{e.grams ? ` · ${hu(e.grams)} g` : ''}
                          <span className="text-gray-400"> ({SLOT_LABEL[e.meal_slot] || e.meal_slot})</span>
                        </span>
                        <span className="shrink-0 font-semibold text-gray-800">{hu(e.kcal)} kcal</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ClientFoodLogCard;
